<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrSkeleton from './HrSkeleton.vue';

/**
 * Primitive wrapper for q-img.
 *
 * Use for standalone content images — program covers, document previews,
 * learning assets, signature thumbnails — where ratio, fit, or loading
 * behavior matters. Not for profile photos in circular containers; use
 * HrAvatar for that pattern.
 *
 * @status recommended
 * @group media
 * @useWhen Displaying content images with aspect ratio control and loading skeleton
 * @avoidWhen Rendering circular profile photos — use HrAvatar instead
 * @see https://quasar.dev/vue-components/img Quasar q-img API
 */
defineOptions({ name: 'HrImg' });

const props = defineProps({
  /**
   * Image URL. When null the component renders nothing (or a skeleton when `pending` is true).
   * @fieldType image
   */
  src: {
    type: String,
    default: null,
  },
  /**
   * Explicit width as a CSS value (e.g. "200px", "100%").
   * Also controls the pending skeleton width so the placeholder matches the image footprint.
   */
  width: {
    type: String,
    default: null,
  },
  /**
   * Explicit height as a CSS value (e.g. "120px").
   * Also controls the pending skeleton height.
   * Usually omitted in favor of `ratio`.
   */
  height: {
    type: String,
    default: null,
  },
  /**
   * Aspect ratio expressed as width/height (e.g. 16/9, 4/3, 24/9).
   * Applied to the pending skeleton via CSS aspect-ratio so the placeholder
   * automatically matches the image shape even without an explicit height.
   */
  ratio: {
    type: Number,
    default: undefined,
    validator: (v) => v === undefined || (Number.isFinite(v) && v > 0),
  },
  /**
   * CSS object-fit value — how the image fills its container.
   * @values cover, contain, fill, none, scale-down
   */
  fit: {
    type: String,
    default: undefined,
    validator: (v) => ['cover', 'contain', 'fill', 'none', 'scale-down'].includes(v),
  },
  /**
   * Renders a rect skeleton placeholder. Size is derived from width/height props
   * and ratio (as CSS aspect-ratio), so the placeholder matches the image footprint.
   */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    ...(props.src !== null && { src: props.src }),
    ...(props.width !== null && { width: props.width }),
    ...(props.height !== null && { height: props.height }),
    ...(props.ratio !== undefined && { ratio: props.ratio }),
    ...(props.fit !== undefined && { fit: props.fit }),
  };
  if (hasTheme.value) return { ...base, ...attrs };
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <hr-skeleton
    v-if="props.pending"
    type="rect"
    v-bind="attrs"
    :width="props.width ?? undefined"
    :height="props.height ?? undefined"
    :style="props.ratio ? { aspectRatio: String(props.ratio) } : undefined"
  />
  <q-img
    v-else
    v-bind="effectiveProps"
    :class="hasTheme ? 'hr-img' : undefined"
  >
    <template
      v-if="$slots.default"
      #default="slotProps"
    >
      <!-- @slot default Overlay content rendered on top of the image. -->
      <slot
        name="default"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.loading"
      #loading="slotProps"
    >
      <!-- @slot loading Custom loading indicator shown while the image is being fetched. -->
      <slot
        name="loading"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.error"
      #error="slotProps"
    >
      <!-- @slot error Content shown when the image fails to load. -->
      <slot
        name="error"
        v-bind="slotProps ?? {}"
      />
    </template>
  </q-img>
</template>
